import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, 
  faFilter,
  faWandMagicSparkles,
  faDollarSign,
  faCalendar,
  faLocationDot,
  faTag,
  faCheck,
  faRulerCombined
} from '@fortawesome/free-solid-svg-icons';
import useI18n from '../hooks/useI18n';
import { UNIT_TYPES } from '../config/unitOfMeasure';
import '../styles/AdvancedFilters.css';

interface AdvancedFiltersProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (filters: FilterState) => void;
  locations: string[];
  tags: string[];
  maxPrice?: number;
}

export interface FilterState {
  condition: string[];
  valueRange: [number, number];
  purchaseDateFrom: string;
  purchaseDateTo: string;
  aiValueTrend: string;
  locations: string[];
  tags: string[];
  hasAIValue: boolean;
  favoritesOnly: boolean;
  minItems: number;
  maxItems: number;
  sortBy: string;
  unitTypes: string[];
}

const AdvancedFilters = ({ isOpen, onClose, onApply, locations, tags, maxPrice = 10000 }: AdvancedFiltersProps) => {
  const { t } = useI18n();
  const [filters, setFilters] = useState<FilterState>({
    condition: [],
    valueRange: [0, maxPrice],
    purchaseDateFrom: '',
    purchaseDateTo: '',
    aiValueTrend: '',
    locations: [],
    tags: [],
    hasAIValue: false,
    favoritesOnly: false,
    minItems: 1,
    maxItems: 1000,
    sortBy: 'newest',
    unitTypes: []
  });

  const conditions = ['All', 'New', 'Like_New', 'Excellent', 'Good', 'Fair', 'Poor'];
  const conditionLabels: Record<string, string> = {
    'All': t('filter.conditionAll'),
    'New': t('condition.new'),
    'Like_New': t('condition.likeNew'),
    'Excellent': t('condition.excellent'),
    'Good': t('condition.good'),
    'Fair': t('condition.fair'),
    'Poor': t('condition.poor'),
  };
  const valueTrends = ['All', 'Appreciating', 'Stable', 'Depreciating'];
  const trendLabels: Record<string, string> = {
    'All': t('filter.trendAll'),
    'Appreciating': t('filter.appreciating'),
    'Stable': t('filter.stable'),
    'Depreciating': t('filter.depreciating'),
  };

  const toggleCondition = (condition: string) => {
    if (condition === 'All') {
      setFilters(prev => ({ ...prev, condition: [] }));
    } else {
      setFilters(prev => ({
        ...prev,
        condition: prev.condition.includes(condition)
          ? prev.condition.filter(c => c !== condition)
          : [...prev.condition, condition]
      }));
    }
  };

  const toggleLocation = (location: string) => {
    setFilters(prev => ({
      ...prev,
      locations: prev.locations.includes(location)
        ? prev.locations.filter(l => l !== location)
        : [...prev.locations, location]
    }));
  };

  const toggleTag = (tag: string) => {
    setFilters(prev => ({
      ...prev,
      tags: prev.tags.includes(tag)
        ? prev.tags.filter(t => t !== tag)
        : [...prev.tags, tag]
    }));
  };

  const toggleUnitType = (unitType: string) => {
    setFilters(prev => ({
      ...prev,
      unitTypes: prev.unitTypes.includes(unitType)
        ? prev.unitTypes.filter(u => u !== unitType)
        : [...prev.unitTypes, unitType]
    }));
  };

  const setValueTrend = (trend: string) => {
    setFilters(prev => ({
      ...prev,
      aiValueTrend: trend === 'All' ? '' : trend
    }));
  };

  const handleReset = () => {
    setFilters({
      condition: [],
      valueRange: [0, maxPrice],
      purchaseDateFrom: '',
      purchaseDateTo: '',
      aiValueTrend: '',
      locations: [],
      tags: [],
      hasAIValue: false,
      favoritesOnly: false,
      minItems: 1,
      maxItems: 1000,
      sortBy: 'newest',
      unitTypes: []
    });
  };

  const handleApply = () => {
    onApply(filters);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="advanced-filters-overlay" onClick={onClose} />
      <div className="advanced-filters-panel">
        <div className="filters-header">
          <h2>
            <FontAwesomeIcon icon={faFilter} /> {t('filter.advancedFilters')}
          </h2>
          <button className="close-btn" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="filters-body">
          {/* Condition */}
          <div className="filter-section">
            <h3>
              <FontAwesomeIcon icon={faCheck} /> {t('filter.condition')}
            </h3>
            <div className="filter-buttons">
              {conditions.map(condition => (
                <button
                  key={condition}
                  className={`filter-btn ${
                    condition === 'All' 
                      ? filters.condition.length === 0 ? 'active' : ''
                      : filters.condition.includes(condition) ? 'active' : ''
                  }`}
                  onClick={() => toggleCondition(condition)}
                >
                  {conditionLabels[condition] || condition}
                </button>
              ))}
            </div>
          </div>

          {/* Value Range */}
          <div className="filter-section">
            <h3>
              <FontAwesomeIcon icon={faDollarSign} /> {t('filter.valueRange')}
            </h3>
            <div className="value-range-inputs">
              <div className="value-range-group">
                <label>{t('filter.min')}</label>
                <input
                  type="number"
                  min="0"
                  max={999999999}
                  step="0.01"
                  placeholder="0"
                  value={filters.valueRange[0] === 0 && document.activeElement !== document.querySelector('.value-range-group:first-child input') ? '' : filters.valueRange[0]}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === '' || raw === '0') {
                      setFilters(prev => ({ ...prev, valueRange: [0, prev.valueRange[1]] }));
                      return;
                    }
                    const val = Math.min(Math.max(0, parseFloat(raw) || 0), 999999999);
                    setFilters(prev => ({
                      ...prev,
                      valueRange: [val, Math.max(val, prev.valueRange[1])]
                    }));
                  }}
                />
              </div>
              <span className="value-range-separator">—</span>
              <div className="value-range-group">
                <label>{t('filter.max')}</label>
                <input
                  type="number"
                  min="0"
                  max={999999999}
                  step="0.01"
                  placeholder="Any"
                  value={filters.valueRange[1] === maxPrice ? '' : filters.valueRange[1]}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === '' || raw === '0') {
                      setFilters(prev => ({ ...prev, valueRange: [Math.min(prev.valueRange[0], 0), 0] }));
                      return;
                    }
                    const val = Math.min(Math.max(0, parseFloat(raw) || 0), 999999999);
                    setFilters(prev => ({
                      ...prev,
                      valueRange: [Math.min(prev.valueRange[0], val), val]
                    }));
                  }}
                />
              </div>
            </div>
          </div>

          {/* Purchase Date */}
          <div className="filter-section">
            <h3>
              <FontAwesomeIcon icon={faCalendar} /> {t('filter.purchaseDate')}
            </h3>
            <div className="date-inputs">
              <div className="date-input-group">
                <label>{t('filter.from')}</label>
                <input
                  type="date"
                  value={filters.purchaseDateFrom}
                  onChange={(e) => setFilters(prev => ({
                    ...prev,
                    purchaseDateFrom: e.target.value
                  }))}
                />
              </div>
              <div className="date-input-group">
                <label>{t('filter.to')}</label>
                <input
                  type="date"
                  value={filters.purchaseDateTo}
                  onChange={(e) => setFilters(prev => ({
                    ...prev,
                    purchaseDateTo: e.target.value
                  }))}
                />
              </div>
            </div>
          </div>

          {/* AI Value Trend */}
          <div className="filter-section">
            <h3>
              <FontAwesomeIcon icon={faWandMagicSparkles} /> {t('filter.aiValueTrend')}
            </h3>
            <div className="filter-buttons">
              {valueTrends.map(trend => (
                <button
                  key={trend}
                  className={`filter-btn ${
                    trend === 'All'
                      ? !filters.aiValueTrend ? 'active' : ''
                      : filters.aiValueTrend === trend ? 'active' : ''
                  }`}
                  onClick={() => setValueTrend(trend)}
                >
                  {trendLabels[trend] || trend}
                </button>
              ))}
            </div>
          </div>

          {/* Locations */}
          {locations.length > 0 && (
            <div className="filter-section">
              <h3>
                <FontAwesomeIcon icon={faLocationDot} /> {t('filter.locations')}
              </h3>
              <div className="filter-tags">
                {locations.map(location => (
                  <button
                    key={location}
                    className={`filter-tag ${filters.locations.includes(location) ? 'active' : ''}`}
                    onClick={() => toggleLocation(location)}
                  >
                    {location}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Tags */}
          {tags.length > 0 && (
            <div className="filter-section">
              <h3>
                <FontAwesomeIcon icon={faTag} /> {t('filter.tags')}
              </h3>
              <div className="filter-tags scrollable">
                {tags.map(tag => (
                  <button
                    key={tag}
                    className={`filter-tag ${filters.tags.includes(tag) ? 'active' : ''}`}
                    onClick={() => toggleTag(tag)}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Unit of Measure */}
          <div className="filter-section">
            <h3>
              <FontAwesomeIcon icon={faRulerCombined} /> {t('unit.title')}
            </h3>
            <div className="filter-buttons">
              {UNIT_TYPES.filter(ut => ut.key !== 'custom').map(ut => (
                <button
                  key={ut.key}
                  className={`filter-btn ${filters.unitTypes.includes(ut.key) ? 'active' : ''}`}
                  onClick={() => toggleUnitType(ut.key)}
                >
                  {t(`unit.type.${ut.key}` as any)}
                </button>
              ))}
            </div>
          </div>

          {/* Additional Options */}
          <div className="filter-section">
            <h3>{t('filter.additionalOptions')}</h3>
            <div className="filter-checkboxes">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={filters.hasAIValue}
                  onChange={(e) => setFilters(prev => ({
                    ...prev,
                    hasAIValue: e.target.checked
                  }))}
                />
                <span>{t('filter.hasAIValue')}</span>
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={filters.favoritesOnly}
                  onChange={(e) => setFilters(prev => ({
                    ...prev,
                    favoritesOnly: e.target.checked
                  }))}
                />
                <span>{t('filter.favoritesOnly')}</span>
              </label>
            </div>
          </div>

          {/* Sort By */}
          <div className="filter-section">
            <h3>{t('filter.sortBy')}</h3>
            <select 
              value={filters.sortBy} 
              onChange={(e) => setFilters(prev => ({ ...prev, sortBy: e.target.value }))}
              className="sort-select"
            >
              <option value="newest">{t('filter.newestFirst')}</option>
              <option value="oldest">{t('filter.oldestFirst')}</option>
              <option value="name-asc">{t('filter.nameAZ')}</option>
              <option value="name-desc">{t('filter.nameZA')}</option>
              <option value="value-high">{t('filter.highestValue')}</option>
              <option value="value-low">{t('filter.lowestValue')}</option>
              <option value="condition-best">{t('filter.bestCondition')}</option>
              <option value="condition-worst">{t('filter.worstCondition')}</option>
            </select>
          </div>
        </div>

        <div className="filters-footer">
          <button className="btn-reset" onClick={handleReset}>
            {t('filter.resetAll')}
          </button>
          <button className="btn-apply" onClick={handleApply}>
            {t('filter.applyFilters')}
          </button>
        </div>
      </div>
    </>
  );
};

export default AdvancedFilters;
